import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, ImageField } from "@/components/admin/fields";

const emptyProduct = {
  name: "", brand_slug: "", category: "", price: "", mrp: "", sale_price: "", sale_start: "", sale_end: "",
  image: "", images: [], description: "", rating: "", badge: "", in_stock: true, instagram_reel: "",
  weight: "", length: "", breadth: "", height: "", variants: [], track_stock: false, stock_qty: "", active: true,
};

const isoToLocal = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const localToIso = (v) => (v ? new Date(v).toISOString() : "");

const Card = ({ title, children, testid }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid={testid}>
    {title && <p className="text-sm font-medium text-[#111827] mb-4">{title}</p>}
    {children}
  </div>
);

export const ProductFormPage = ({ initial, brands, onBack, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(() => {
    if (!initial) return { ...emptyProduct, brand_slug: brands[0]?.slug || "" };
    return {
      ...initial,
      mrp: initial.mrp ?? "", rating: initial.rating ?? "",
      sale_price: initial.sale_price ?? "",
      sale_start: isoToLocal(initial.sale_start), sale_end: isoToLocal(initial.sale_end),
      weight: initial.weight ?? "", length: initial.length ?? "", breadth: initial.breadth ?? "", height: initial.height ?? "",
      stock_qty: initial.stock_qty ?? "",
      variants: (initial.variants || []).map((v) => ({ title: v.title, price: v.price ?? "" })),
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const setVariant = (i, k, v) => setForm((f) => ({ ...f, variants: f.variants.map((row, j) => (j === i ? { ...row, [k]: v } : row)) }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      price: Number(form.price),
      mrp: form.mrp === "" ? null : Number(form.mrp),
      rating: form.rating === "" ? null : Number(form.rating),
      sale_price: form.sale_price === "" ? null : Number(form.sale_price),
      sale_start: localToIso(form.sale_start),
      sale_end: localToIso(form.sale_end),
      weight: form.weight === "" ? null : Number(form.weight),
      length: form.length === "" ? null : Number(form.length),
      breadth: form.breadth === "" ? null : Number(form.breadth),
      height: form.height === "" ? null : Number(form.height),
      stock_qty: form.stock_qty === "" ? null : parseInt(form.stock_qty, 10),
      variants: form.variants.filter((v) => v.title.trim()).map((v) => ({ title: v.title.trim(), price: v.price === "" ? null : Number(v.price) })),
    };
    try {
      if (isEdit) await api.put(`/admin/products/${initial.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved();
      onBack();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E80071]/30 focus:border-[#E80071]";

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="max-w-[1100px]" data-testid="product-form-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} data-testid="product-form-back" className="p-2 rounded-lg hover:bg-gray-200/60 text-gray-600 transition-colors" aria-label="Back to products">
          <ArrowLeft size={17} />
        </button>
        <h1 className="text-xl font-semibold tracking-tight text-[#111827]">{isEdit ? `Edit — ${initial.name}` : "New product"}</h1>
      </div>

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <Card title="Details" testid="pf-details">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Title *</label>
                <input required value={form.name} onChange={set("name")} data-testid="product-form-name" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Description</label>
                <textarea rows={4} value={form.description} onChange={set("description")} data-testid="product-form-description" className={inputCls} />
              </div>
            </div>
          </Card>

          <Card title="Media" testid="pf-media">
            <ImageField value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testid="product-form-image" />
            <div className="mt-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Gallery URLs (one per line)</label>
              <textarea
                rows={2}
                value={Array.isArray(form.images) ? form.images.join("\n") : ""}
                onChange={(e) => setForm((f) => ({ ...f, images: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) }))}
                data-testid="product-form-gallery"
                className={inputCls}
              />
            </div>
          </Card>

          <Card title="Pricing" testid="pf-pricing">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Selling price (₹) *" required type="number" step="0.01" value={form.price} onChange={set("price")} data-testid="product-form-price" />
              <Field label="Compare-at / MRP (₹)" type="number" step="0.01" value={form.mrp} onChange={set("mrp")} data-testid="product-form-mrp" />
              <Field label="Special sale price (₹)" type="number" step="0.01" value={form.sale_price} onChange={set("sale_price")} data-testid="product-form-sale-price" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Sale starts</label>
                <input type="datetime-local" value={form.sale_start} onChange={set("sale_start")} data-testid="product-form-sale-start" className={inputCls} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Sale ends</label>
                <input type="datetime-local" value={form.sale_end} onChange={set("sale_end")} data-testid="product-form-sale-end" className={inputCls} />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-3">The special price automatically replaces the selling price between these dates — perfect for festival or prime-day sales.</p>
          </Card>

          <Card title="Variants / sizes" testid="pf-variants">
            {form.variants.length === 0 && <p className="text-xs text-gray-400 mb-3">No variants — the product sells at one size/price. Add variants like 50ml / 100ml or S / M / L.</p>}
            <div className="space-y-3">
              {form.variants.map((v, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`variant-row-${i}`}>
                  <input value={v.title} onChange={(e) => setVariant(i, "title", e.target.value)} placeholder="e.g. 100ml or Large" data-testid={`variant-title-${i}`} className={inputCls} />
                  <input value={v.price} onChange={(e) => setVariant(i, "price", e.target.value)} placeholder="Price ₹ (blank = base)" type="number" step="0.01" data-testid={`variant-price-${i}`} className={inputCls} />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }))} data-testid={`variant-remove-${i}`} className="p-2 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove variant">
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setForm((f) => ({ ...f, variants: [...f.variants, { title: "", price: "" }] }))} data-testid="variant-add-btn" className="mt-3 flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm">
              <Plus size={14} /> Add variant
            </button>
          </Card>

          <Card title="Shipping (optional)" testid="pf-shipping">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Field label="Weight (kg)" type="number" step="0.01" value={form.weight} onChange={set("weight")} placeholder="0.3" data-testid="product-form-weight" />
              <Field label="Length (cm)" type="number" step="0.1" value={form.length} onChange={set("length")} placeholder="15" data-testid="product-form-length" />
              <Field label="Breadth (cm)" type="number" step="0.1" value={form.breadth} onChange={set("breadth")} placeholder="12" data-testid="product-form-breadth" />
              <Field label="Height (cm)" type="number" step="0.1" value={form.height} onChange={set("height")} placeholder="8" data-testid="product-form-height" />
            </div>
            <p className="text-xs text-gray-400 mt-3">Used to calculate courier charges automatically when Shiprocket is connected.</p>
          </Card>
        </div>

        <div className="flex flex-col gap-6">
          <Card title="Status" testid="pf-status">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input type="checkbox" checked={form.active} onChange={set("active")} data-testid="product-form-active" className="accent-[#E80071] w-4 h-4" />
              Active (visible in the store)
            </label>
            <p className="text-xs text-gray-400 mt-2">Turn off to hide the product without deleting it.</p>
            <label className="flex items-center gap-3 text-sm text-gray-700 mt-4">
              <input type="checkbox" checked={form.in_stock} onChange={set("in_stock")} data-testid="product-form-stock" className="accent-[#E80071] w-4 h-4" />
              Available to buy
            </label>
          </Card>

          <Card title="Inventory" testid="pf-inventory">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input type="checkbox" checked={form.track_stock} onChange={set("track_stock")} data-testid="product-form-track-stock" className="accent-[#E80071] w-4 h-4" />
              Track quantity
            </label>
            {form.track_stock && (
              <div className="mt-3">
                <Field label="Quantity in stock" type="number" value={form.stock_qty} onChange={set("stock_qty")} data-testid="product-form-stock-qty" />
                <p className="text-xs text-gray-400 mt-2">Decreases automatically on every paid order. Hits zero → marked out of stock.</p>
              </div>
            )}
          </Card>

          <Card title="Organization" testid="pf-organization">
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 block mb-1.5">Brand</label>
                <select value={form.brand_slug} onChange={set("brand_slug")} data-testid="product-form-brand" className={inputCls}>
                  {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
                </select>
              </div>
              <Field label="Category" value={form.category} onChange={set("category")} data-testid="product-form-category" />
              <Field label="Rating (0–5)" type="number" step="0.1" min="0" max="5" value={form.rating} onChange={set("rating")} data-testid="product-form-rating" />
              <Field label="Badge" value={form.badge} onChange={set("badge")} placeholder="Bestseller, New…" data-testid="product-form-badge" />
              <Field label="Instagram Reel URL (tagged to this product)" value={form.instagram_reel || ""} onChange={set("instagram_reel")} placeholder="https://www.instagram.com/reel/…" data-testid="product-form-reel" />
            </div>
          </Card>

          <button type="submit" disabled={saving} data-testid="product-form-submit" className="bg-[#E80071] hover:bg-[#D00065] text-white px-4 py-3 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create product"}
          </button>
        </div>
      </form>
    </motion.div>
  );
};
